/**
 * Mathematical Equations generator (EXAM.md §4, PLAN.md §2a).
 *
 * Letters A–D, each an integer 1–20, exactly one solution. Generation is
 * solution-first: draw the letter values, then build equations from templates
 * shaped by difficulty that those values satisfy. Every candidate is verified
 * to have exactly ONE solution over the whole 1–20 domain — a wrong question
 * teaches the wrong thing, so this is the most important check in the app.
 *
 * Uniqueness counting (`countSolutions`) exhaustively enumerates the 1–20ⁿ
 * domain with constraint-ordered backtracking and early exit at a second
 * solution — for linear systems this is equivalent to (and much faster than)
 * a naive 20ⁿ scan; the naive scan is used independently in the test suite.
 *
 * This module must stay DOM-free and dependency-free (PLAN-Z convention 4).
 */
import { mulberry32, pick, randomInt, shuffle } from './prng.ts'
import type { Difficulty, Question, Step } from './types.ts'

/** Coefficients kept small so the arithmetic stays mental (EXAM.md §4). */
const COEFFS = [2, 3, 4, 5, 10] as const

export interface EquationsPrompt {
  equations: string[]
  letters: string[]
  /** How many equations must be substituted into another before solving can start. */
  substitutionSteps: number
}

export interface EquationsAnswer {
  values: Record<string, number>
}

interface LinTerm {
  letter: string
  coef: number
}

/** Σ(coef·letter) = rhs, with integer coefficients. */
interface LinEquation {
  terms: LinTerm[]
  rhs: number
}

/** A built equation: display text, normalised linear form, and expressions
 * isolating each of its letters in terms of the other (for explanations). */
interface Built {
  text: string
  lin: LinEquation
  expr: Record<string, (other: string | number) => string>
}

interface BuiltItem {
  eqs: Built[]
  letters: string[]
  values: Record<string, number>
  substitutionSteps: number
  explanation: Step[]
}

type Draw = () => number

const fmtCoef = (c: number, x: string) => (c === 1 ? x : `${c} × ${x}`)
const fmtNum = (c: number, x: number) => (c === 1 ? `${x}` : `${c} × ${x}`)

function lin1(letter: string, coef: number, rhs: number): LinEquation {
  return { terms: [{ letter, coef }], rhs }
}

// ---------------------------------------------------------------------------
// Uniqueness: exhaustive count over the 1–20ⁿ domain (early exit at 2nd).
// ---------------------------------------------------------------------------

export function countSolutions(eqs: LinEquation[], letters: string[], limit = 2): number {
  const idx = new Map(letters.map((l, i) => [l, i] as const))
  const n = letters.length
  const norm = eqs.map((eq) => ({
    mask: eq.terms.reduce((m, t) => m | (1 << idx.get(t.letter)!), 0),
    coefs: eq.terms.map((t) => [idx.get(t.letter)!, t.coef] as const),
    rhs: eq.rhs,
  }))
  // Letter order that completes equations early: take letters equation by
  // equation, cheapest equations first, so pruning kicks in after few draws.
  const order: number[] = []
  const seen = new Set<number>()
  for (const eq of [...norm].sort((x, y) => x.coefs.length - y.coefs.length)) {
    for (const [i] of eq.coefs) {
      if (!seen.has(i)) {
        seen.add(i)
        order.push(i)
      }
    }
  }
  for (let i = 0; i < n; i++) {
    if (!seen.has(i)) {
      seen.add(i)
      order.push(i)
    }
  }
  const depthMask: number[] = [0]
  for (let d = 0; d < n; d++) depthMask.push(depthMask[d] | (1 << order[d]))

  const vals = new Array<number>(n).fill(0)
  let count = 0
  const rec = (depth: number): void => {
    if (depth === n) {
      count++
      return
    }
    const mask = depthMask[depth + 1]
    const L = order[depth]
    for (let v = 1; v <= 20; v++) {
      vals[L] = v
      let ok = true
      for (const eq of norm) {
        if ((eq.mask & mask) !== eq.mask) continue
        let s = 0
        for (const [i, c] of eq.coefs) s += c * vals[i]
        if (s !== eq.rhs) {
          ok = false
          break
        }
      }
      if (ok) {
        rec(depth + 1)
        if (count >= limit) return
      }
    }
  }
  rec(0)
  return count
}

// ---------------------------------------------------------------------------
// Equation forms. Every displayed constant stays ≤ 20; ÷ divides evenly.
// ---------------------------------------------------------------------------

/** One equation that gives letter X (value v) in ≤ 1 step from constants. */
function makeIso(draw: Draw, X: string, v: number): Built {
  const forms: Built[] = []
  if (v <= 19) {
    const k = randomInt(draw, 1, 20 - v)
    forms.push({
      text: `${k} + ${X} = ${k + v}`,
      lin: lin1(X, 1, v),
      expr: { [X]: () => `${k + v} − ${k}` },
    })
  }
  if (v >= 2) {
    const k = randomInt(draw, 1, v - 1)
    forms.push({
      text: `${X} − ${k} = ${v - k}`,
      lin: lin1(X, 1, v),
      expr: { [X]: () => `${v - k} + ${k}` },
    })
  }
  const multKs = COEFFS.filter((c) => c * v <= 20)
  if (multKs.length > 0) {
    const k = pick(draw, multKs)
    forms.push({
      text: `${k} × ${X} = ${k * v}`,
      lin: lin1(X, k, k * v),
      expr: { [X]: () => `${k * v} ÷ ${k}` },
    })
  }
  const divKs = COEFFS.filter((c) => c < v && v % c === 0)
  if (divKs.length > 0) {
    const k = pick(draw, divKs)
    forms.push({
      text: `${X} ÷ ${k} = ${v / k}`,
      lin: lin1(X, 1, v),
      expr: { [X]: () => `${v / k} × ${k}` },
    })
  }
  return pick(draw, forms)
}

/** X − Y = d with d = vx − vy ≥ 1. */
function relAdd(P: string, Q: string, d: number, text: string): Built {
  return {
    text,
    lin: { terms: [{ letter: P, coef: 1 }, { letter: Q, coef: -1 }], rhs: d },
    expr: {
      [P]: (q) => `${q} + ${d}`,
      [Q]: (p) => `${p} − ${d}`,
    },
  }
}

/** k − X = Y and k − Y = X with k = vx + vy ≤ 19. */
function relCm(X: string, Y: string, k: number, text: string): Built {
  return {
    text,
    lin: { terms: [{ letter: X, coef: 1 }, { letter: Y, coef: 1 }], rhs: k },
    expr: {
      [X]: (y) => `${k} − ${y}`,
      [Y]: (x) => `${k} − ${x}`,
    },
  }
}

/** Y = m × X (m ∈ COEFFS, vy = m·vx). */
function relMult(X: string, Y: string, m: number, text: string): Built {
  return {
    text,
    lin: { terms: [{ letter: X, coef: m }, { letter: Y, coef: -1 }], rhs: 0 },
    expr: {
      [Y]: (x) => `${m} × ${x}`,
      [X]: (y) => `${y} ÷ ${m}`,
    },
  }
}

/** A relation between letters X and Y, feasible for their values; null if none. */
function makeRelation(draw: Draw, X: string, vx: number, Y: string, vy: number): Built | null {
  const forms: Built[] = []
  const d = Math.abs(vx - vy)
  if (d >= 1) {
    if (vy > vx) {
      forms.push(relAdd(Y, X, d, `${Y} − ${X} = ${d}`))
      forms.push(relAdd(Y, X, d, `${Y} − ${d} = ${X}`))
      forms.push(relAdd(Y, X, d, `${X} + ${d} = ${Y}`))
    } else {
      forms.push(relAdd(X, Y, d, `${X} − ${Y} = ${d}`))
      forms.push(relAdd(X, Y, d, `${X} − ${d} = ${Y}`))
      forms.push(relAdd(X, Y, d, `${Y} + ${d} = ${X}`))
    }
  }
  if (vx + vy <= 19) {
    forms.push(relCm(X, Y, vx + vy, `${vx + vy} − ${Y} = ${X}`))
    forms.push(relCm(X, Y, vx + vy, `${vx + vy} − ${X} = ${Y}`))
  }
  const mY = vy % vx === 0 ? vy / vx : 0
  const mX = vx % vy === 0 ? vx / vy : 0
  if (mY >= 2 && (COEFFS as readonly number[]).includes(mY)) {
    forms.push(relMult(X, Y, mY, `${mY} × ${X} = ${Y}`))
    forms.push(relMult(X, Y, mY, `${Y} ÷ ${mY} = ${X}`))
  }
  if (mX >= 2 && (COEFFS as readonly number[]).includes(mX)) {
    forms.push(relMult(Y, X, mX, `${mX} × ${Y} = ${X}`))
    forms.push(relMult(Y, X, mX, `${X} ÷ ${mX} = ${Y}`))
  }
  return forms.length > 0 ? pick(draw, forms) : null
}

// ---------------------------------------------------------------------------
// Difficulty variants. Each returns null when the drawn values admit no
// template; the caller redraws.
// ---------------------------------------------------------------------------

/** low: 2 letters, 2 equations — one equation gives A in ≤ 1 step, the second relates B to A. */
function buildLow(draw: Draw): BuiltItem | null {
  const a = randomInt(draw, 1, 20)
  const b = randomInt(draw, 1, 20)
  const eqA = makeIso(draw, 'A', a)
  const eqRel = makeRelation(draw, 'A', a, 'B', b)
  if (!eqRel) return null
  const eqs = shuffle(draw, [eqA, eqRel])
  const iA = eqs.indexOf(eqA) + 1
  const iR = eqs.indexOf(eqRel) + 1
  return {
    eqs,
    letters: ['A', 'B'],
    values: { A: a, B: b },
    substitutionSteps: 0,
    explanation: [
      { text: `Eq. ${iA}: A = ${eqA.expr['A'](0)} = ${a}.` },
      { text: `Eq. ${iR}: B = ${eqRel.expr['B'](a)} = ${b}.` },
    ],
  }
}

/**
 * medium, official-Exercise-4 shape: two equations mutually relate A and B
 * (one multiplicative, one additive) so one must be substituted into the
 * other; the third defines C from the solved pair.
 */
function buildMediumM1(draw: Draw): BuiltItem | null {
  const k = pick(draw, COEFFS)
  const bIsK = draw() < 0.5
  let a: number
  let b: number
  if (bIsK) {
    a = randomInt(draw, 1, Math.floor(20 / k))
    b = k * a
  } else {
    b = randomInt(draw, 1, Math.floor(20 / k))
    a = k * b
  }
  const r1 = bIsK
    ? pick(draw, [
        relMult('A', 'B', k, `${k} × A = B`),
        relMult('A', 'B', k, `B ÷ ${k} = A`),
      ])
    : pick(draw, [
        relMult('B', 'A', k, `${k} × B = A`),
        relMult('B', 'A', k, `A ÷ ${k} = B`),
      ])
  const r2 =
    a + b <= 19 && draw() < 0.5
      ? pick(draw, [
          relCm('A', 'B', a + b, `${a + b} − B = A`),
          relCm('A', 'B', a + b, `${a + b} − A = B`),
        ])
      : a > b
        ? pick(draw, [
            relAdd('A', 'B', a - b, `A − B = ${a - b}`),
            relAdd('A', 'B', a - b, `A − ${a - b} = B`),
            relAdd('A', 'B', a - b, `B + ${a - b} = A`),
          ])
        : pick(draw, [
            relAdd('B', 'A', b - a, `B − A = ${b - a}`),
            relAdd('B', 'A', b - a, `B − ${b - a} = A`),
            relAdd('B', 'A', b - a, `A + ${b - a} = B`),
          ])
  // Third equation defines C from A or B.
  let r3: Built | null = null
  let c = 0
  let anchorVal = 0
  for (let i = 0; i < 50 && !r3; i++) {
    c = randomInt(draw, 1, 20)
    const useA = draw() < 0.5
    const cand = useA ? makeRelation(draw, 'A', a, 'C', c) : makeRelation(draw, 'B', b, 'C', c)
    if (cand) {
      r3 = cand
      anchorVal = useA ? a : b
    }
  }
  if (!r3) return null

  // Substitution algebra: r1 says L1 = k·L2; substitute into r2.
  const L1 = bIsK ? 'B' : 'A'
  const L2 = bIsK ? 'A' : 'B'
  const L1val = bIsK ? b : a
  const L2val = bIsK ? a : b
  const alpha = r2.lin.terms.find((t) => t.letter === L1)!.coef
  const beta = r2.lin.terms.find((t) => t.letter === L2)!.coef
  const newCoef = alpha * k + beta
  const gamma = r2.lin.rhs
  if (newCoef < 2 || newCoef > 13 || gamma % newCoef !== 0 || gamma / newCoef !== L2val) return null

  const eqs = shuffle(draw, [r1, r2, r3])
  const i1 = eqs.indexOf(r1) + 1
  const i2 = eqs.indexOf(r2) + 1
  const i3 = eqs.indexOf(r3) + 1
  return {
    eqs,
    letters: ['A', 'B', 'C'],
    values: { A: a, B: b, C: c },
    substitutionSteps: 1,
    explanation: [
      {
        text: `Eq. ${i1} gives ${L1} = ${r1.expr[L1](`${L2}`)}. Substitute into eq. ${i2}:`,
        lines: [
          `${fmtCoef(newCoef, L2)} = ${gamma}`,
          `${L2} = ${gamma} ÷ ${newCoef}`,
          `${L2} = ${L2val}`,
        ],
      },
      { text: `${L1} = ${r1.expr[L1](L2val)} = ${L1val}.` },
      { text: `Eq. ${i3}: C = ${r3.expr['C'](anchorVal)} = ${c}.` },
    ],
  }
}

/**
 * medium, official-Exercise-3 shape: A = k × C and A + C = s force C by
 * substitution; B is then defined by a compound equation p·A ± q·C = B.
 */
function buildMediumM2(draw: Draw): BuiltItem | null {
  const k = pick(draw, COEFFS)
  const cMax = Math.floor(19 / (k + 1))
  if (cMax < 1) return null
  const c = randomInt(draw, 1, cMax)
  const a = k * c
  const s = a + c
  const sumAsConstMinus = draw() < 0.5
  const eq2: Built = sumAsConstMinus
    ? {
        text: `${s} − C = A`,
        lin: { terms: [{ letter: 'A', coef: 1 }, { letter: 'C', coef: 1 }], rhs: s },
        expr: {},
      }
    : {
        text: `A + C = ${s}`,
        lin: { terms: [{ letter: 'A', coef: 1 }, { letter: 'C', coef: 1 }], rhs: s },
        expr: {},
      }
  const opts: { p: number; q: number; op: string; val: number }[] = []
  for (const p of [1, 2, 3]) {
    for (const q of [1, 2, 3]) {
      for (const op of ['+', '−']) {
        const val = op === '+' ? p * a + q * c : p * a - q * c
        if (val >= 1 && val <= 20) opts.push({ p, q, op, val })
      }
    }
  }
  if (opts.length === 0) return null
  const { p, q, op, val: b } = pick(draw, opts)
  const eq3: Built = {
    text: `${fmtCoef(p, 'A')} ${op} ${fmtCoef(q, 'C')} = B`,
    lin: {
      terms: [
        { letter: 'A', coef: p },
        { letter: 'C', coef: op === '+' ? q : -q },
        { letter: 'B', coef: -1 },
      ],
      rhs: 0,
    },
    expr: {},
  }
  const eq1 = relMult('C', 'A', k, `${k} × C = A`)
  const eqs = shuffle(draw, [eq1, eq2, eq3])
  const i1 = eqs.indexOf(eq1) + 1
  const i2 = eqs.indexOf(eq2) + 1
  const i3 = eqs.indexOf(eq3) + 1
  return {
    eqs,
    letters: ['A', 'B', 'C'],
    values: { A: a, B: b, C: c },
    substitutionSteps: 1,
    explanation: [
      {
        text: `Eq. ${i1} gives A = ${k} × C. Substitute into eq. ${i2}:`,
        lines: [
          ...(sumAsConstMinus ? [`${s} − C = ${k} × C`] : [`${k} × C + C = ${s}`]),
          `${fmtCoef(k + 1, 'C')} = ${s}`,
          `C = ${s} ÷ ${k + 1}`,
          `C = ${c}`,
        ],
      },
      { text: `A = ${k} × ${c} = ${a}.` },
      { text: `Eq. ${i3}: B = ${fmtNum(p, a)} ${op} ${fmtNum(q, c)} = ${b}.` },
    ],
  }
}

// ---------------------------------------------------------------------------
// high: pivot letter, three definitions, one combined target equation.
// ---------------------------------------------------------------------------

interface Def {
  letter: string
  /** d(letter)/d(pivot). */
  alpha: number
  /** constant part of the definition. */
  beta: number
  val: number
  /** Substituted form, e.g. "5 × B" or "(11 + B)". */
  sub: string
  back: string
}

/** Substituted term for a def at target position with sign s. */
function defTerm(def: Def, sign: number): string {
  const core =
    def.beta === 0
      ? def.alpha === 1
        ? def.letter
        : `${def.alpha} × ${def.letter}`
      : `(${def.beta} ${def.alpha === -1 ? '−' : '+'} ${def.letter})`
  return sign > 0 ? core : `− ${core}`
}

function targetText(letters: string[], signs: number[], t: number): string {
  const lhs = letters
    .map((L, i) => `${i === 0 ? '' : signs[i] > 0 ? ' + ' : ' − '}${L}`)
    .join('')
  return `${lhs} = ${t}`
}

function pickTarget(
  draw: Draw,
  defs: Def[],
  size: number,
): { letters: string[]; signs: number[]; t: number; cpivot: number; constTerm: number } | null {
  const cands: { letters: string[]; signs: number[]; t: number; cpivot: number; constTerm: number }[] = []
  for (const perm of permutations(defs.map((d) => d.letter))) {
    for (const signs of signPatterns(size)) {
      let t = 0
      let cpivot = 0
      let constTerm = 0
      for (let i = 0; i < size; i++) {
        const def = defs.find((d) => d.letter === perm[i])!
        t += signs[i] * def.val
        cpivot += signs[i] * def.alpha
        constTerm += signs[i] * def.beta
      }
      if (t >= 1 && t <= 19 && cpivot >= 2 && cpivot <= 13 && Math.abs(constTerm) <= 19) {
        cands.push({ letters: perm, signs, t, cpivot, constTerm })
      }
    }
  }
  return cands.length > 0 ? pick(draw, cands) : null
}

function permutations<T>(items: T[]): T[][] {
  if (items.length <= 1) return [items.slice()]
  const out: T[][] = []
  for (let i = 0; i < items.length; i++) {
    const rest = [...items.slice(0, i), ...items.slice(i + 1)]
    for (const p of permutations(rest)) out.push([items[i], ...p])
  }
  return out
}

function signPatterns(n: number): number[][] {
  const out: number[][] = []
  for (let bits = 0; bits < 2 ** (n - 1); bits++) {
    const signs = [1]
    for (let i = 1; i < n; i++) signs.push(bits & (1 << (i - 1)) ? 1 : -1)
    out.push(signs)
  }
  return out
}

/**
 * high, official-Exercise-5 shape: A and C are multiples of the pivot B, D is
 * B ± k, and the target equation combines all four letters, requiring a
 * bracket expansion on substitution.
 */
function buildHighH1(draw: Draw): BuiltItem | null {
  const k1 = pick(draw, COEFFS)
  const k2 = pick(draw, COEFFS.filter((x) => x !== k1))
  const bMax = Math.floor(20 / Math.max(k1, k2))
  const b = randomInt(draw, 1, bMax)
  const a = k1 * b
  const c = k2 * b
  const useMinus = draw() < 0.5
  const k3 = useMinus ? randomInt(draw, b + 1, 19) : randomInt(draw, 1, 20 - b)
  const d = useMinus ? k3 - b : k3 + b
  const pivot: Def = { letter: 'B', alpha: 1, beta: 0, val: b, sub: 'B', back: '' }
  const defA: Def = { letter: 'A', alpha: k1, beta: 0, val: a, sub: `${k1} × B`, back: `A = ${k1} × ${b} = ${a}.` }
  const defC: Def = { letter: 'C', alpha: k2, beta: 0, val: c, sub: `${k2} × B`, back: `C = ${k2} × ${b} = ${c}.` }
  const defD: Def = {
    letter: 'D',
    alpha: useMinus ? -1 : 1,
    beta: k3,
    val: d,
    sub: `(${k3} ${useMinus ? '−' : '+'} B)`,
    back: useMinus ? `D = ${k3} − ${b} = ${d}.` : `D = ${k3} + ${b} = ${d}.`,
  }
  const cand = pickTarget(draw, [pivot, defA, defC, defD], 4)
  if (!cand) return null
  const { letters, signs, t, cpivot, constTerm } = cand
  const adjusted = cpivot * b
  const target: Built = {
    text: targetText(letters, signs, t),
    lin: { terms: letters.map((L, i) => ({ letter: L, coef: signs[i] })), rhs: t },
    expr: {},
  }
  const defEqs: Built[] = [
    { text: `${k1} × B = A`, lin: relLinFromDef(defA, 'B'), expr: {} },
    { text: `${k2} × B = C`, lin: relLinFromDef(defC, 'B'), expr: {} },
    useMinus
      ? { text: `${k3} − B = D`, lin: relLinFromDef(defD, 'B'), expr: {} }
      : { text: `${k3} + B = D`, lin: relLinFromDef(defD, 'B'), expr: {} },
  ]
  const eqs = [target, ...shuffle(draw, defEqs)]
  const allDefs = [pivot, defA, defC, defD]
  const substituted = letters.map((L, i) => defTerm(allDefs.find((x) => x.letter === L)!, signs[i])).join(' ')
  return {
    eqs,
    letters: ['A', 'B', 'C', 'D'],
    values: { A: a, B: b, C: c, D: d },
    substitutionSteps: 3,
    explanation: [
      {
        text: `Substitute A = ${k1} × B, C = ${k2} × B and D = ${useMinus ? `${k3} − B` : `${k3} + B`} into eq. 1:`,
        lines: [
          `${substituted} = ${t}`,
          `${cpivot} × B ${constTerm > 0 ? '+' : '−'} ${Math.abs(constTerm)} = ${adjusted}`,
          `B = ${adjusted} ÷ ${cpivot}`,
          `B = ${b}`,
        ],
      },
      { text: defA.back },
      { text: defC.back },
      { text: defD.back },
    ],
  }
}

/** LinEquation for "def = α·pivot + β" against the pivot letter. */
function relLinFromDef(def: Def, pivot: string): LinEquation {
  return {
    terms: [
      { letter: pivot, coef: -def.alpha },
      { letter: def.letter, coef: 1 },
    ],
    rhs: def.beta,
  }
}

/**
 * high, official-Exercise-6 shape: pivot with a multiplicative def, a
 * constant-offset def and a compound def (k × P ± m); the target combines
 * only three of the letters.
 */
function buildHighH2(draw: Draw): BuiltItem | null {
  const pivotLetter = pick(draw, ['B', 'C'] as const)
  const k1 = pick(draw, COEFFS)
  const pMax = Math.floor(20 / k1)
  const p = randomInt(draw, 1, pMax)
  const useMinus = draw() < 0.5
  const k2 = useMinus ? randomInt(draw, p + 1, 19) : randomInt(draw, 1, 20 - p)
  const offVal = useMinus ? k2 - p : k2 + p
  // Compound def: k3 × P ± m = Q, 1 ≤ q ≤ 20.
  const compOpts: { k3: number; m: number; op: string; q: number }[] = []
  for (const k3 of COEFFS) {
    for (const m of [1, 2, 3]) {
      const plus = k3 * p + m
      const minus = k3 * p - m
      if (plus <= 20) compOpts.push({ k3, m, op: '+', q: plus })
      if (minus >= 1) compOpts.push({ k3, m, op: '−', q: minus })
    }
  }
  if (compOpts.length === 0) return null
  const { k3, m, op, q } = pick(draw, compOpts)

  const other = (['A', 'B', 'C', 'D'] as const).filter((L) => L !== pivotLetter)
  const defM: Def = { letter: other[0], alpha: k1, beta: 0, val: k1 * p, sub: `${k1} × ${pivotLetter}`, back: `${other[0]} = ${k1} × ${p} = ${k1 * p}.` }
  const defL: Def = {
    letter: other[1],
    alpha: useMinus ? -1 : 1,
    beta: k2,
    val: offVal,
    sub: `(${k2} ${useMinus ? '−' : '+'} ${pivotLetter})`,
    back: useMinus ? `${other[1]} = ${k2} − ${p} = ${offVal}.` : `${other[1]} = ${k2} + ${p} = ${offVal}.`,
  }
  const defQ: Def = {
    letter: other[2],
    alpha: k3,
    beta: op === '+' ? m : -m,
    val: q,
    sub: '',
    back: `${other[2]} = ${fmtNum(k3, p)} ${op} ${m} = ${q}.`,
  }
  const pivotDef: Def = { letter: pivotLetter, alpha: 1, beta: 0, val: p, sub: pivotLetter, back: '' }
  const cand = pickTarget(draw, [pivotDef, defM, defL], 3)
  if (!cand) return null
  const { letters, signs, t, cpivot, constTerm } = cand
  const adjusted = cpivot * p
  const target: Built = {
    text: targetText(letters, signs, t),
    lin: { terms: letters.map((L, i) => ({ letter: L, coef: signs[i] })), rhs: t },
    expr: {},
  }
  const defEqs: Built[] = [
    { text: `${k1} × ${pivotLetter} = ${other[0]}`, lin: relLinFromDef(defM, pivotLetter), expr: {} },
    useMinus
      ? { text: `${k2} − ${pivotLetter} = ${other[1]}`, lin: relLinFromDef(defL, pivotLetter), expr: {} }
      : { text: `${k2} + ${pivotLetter} = ${other[1]}`, lin: relLinFromDef(defL, pivotLetter), expr: {} },
    {
      text: `${fmtCoef(k3, pivotLetter)} ${op} ${m} = ${other[2]}`,
      lin: relLinFromDef(defQ, pivotLetter),
      expr: {},
    },
  ]
  const eqs = [target, ...shuffle(draw, defEqs)]
  const substituted = letters
    .map((L, i) => defTerm(defsOf(pivotDef, defM, defL)(L)!, signs[i]))
    .join(' ')
  const vals: Record<string, number> = { [pivotLetter]: p, [other[0]]: k1 * p, [other[1]]: offVal, [other[2]]: q }
  return {
    eqs,
    letters: ['A', 'B', 'C', 'D'],
    values: vals,
    substitutionSteps: 2,
    explanation: [
      {
        text: `Substitute ${other[0]} = ${k1} × ${pivotLetter} and ${other[1]} = ${useMinus ? `${k2} − ${pivotLetter}` : `${k2} + ${pivotLetter}`} into eq. 1:`,
        lines: [
          `${substituted} = ${t}`,
          `${cpivot} × ${pivotLetter} ${constTerm > 0 ? '+' : '−'} ${Math.abs(constTerm)} = ${adjusted}`,
          `${pivotLetter} = ${adjusted} ÷ ${cpivot}`,
          `${pivotLetter} = ${p}`,
        ],
      },
      { text: defM.back },
      { text: defL.back },
      { text: defQ.back },
    ],
  }
}

function defsOf(...defs: Def[]): (letter: string) => Def | undefined {
  return (letter) => defs.find((d) => d.letter === letter)
}

// ---------------------------------------------------------------------------

const LETTERS: Record<Difficulty, string[]> = {
  low: ['A', 'B'],
  medium: ['A', 'B', 'C'],
  high: ['A', 'B', 'C', 'D'],
}

/**
 * Generate one equations item. Pure: same (difficulty, seed) → same question.
 * Reject-and-redraw until the system has exactly one solution in 1–20ⁿ.
 */
export function generateEquations(
  difficulty: Difficulty,
  seed: number,
): Question<EquationsPrompt, EquationsAnswer> {
  const draw = mulberry32(seed)
  for (let attempt = 0; attempt < 1000; attempt++) {
    const built =
      difficulty === 'low'
        ? buildLow(draw)
        : difficulty === 'medium'
          ? draw() < 0.5
            ? buildMediumM1(draw)
            : buildMediumM2(draw)
          : draw() < 0.5
            ? buildHighH1(draw)
            : buildHighH2(draw)
    if (!built) continue
    if (built.letters.join(',') !== LETTERS[difficulty].join(',')) continue
    // The official items never repeat a left-hand side; reject look-alike pairs.
    const lhsSet = new Set(built.eqs.map((e) => e.text.split(' = ')[0]))
    if (lhsSet.size !== built.eqs.length) continue
    const lins = built.eqs.map((e) => e.lin)
    if (countSolutions(lins, built.letters) !== 1) continue
    // Construction-bug guard: the drawn values must satisfy every equation.
    const satisfied = lins.every((eq) =>
      eq.terms.reduce((s, t) => s + t.coef * built.values[t.letter], 0) === eq.rhs,
    )
    if (!satisfied) continue
    return {
      subtest: 'equations',
      difficulty,
      seed,
      prompt: {
        equations: built.eqs.map((e) => e.text),
        letters: built.letters,
        substitutionSteps: built.substitutionSteps,
      },
      answer: { values: built.values },
      explanation: built.explanation,
    }
  }
  throw new Error(`equations: failed to construct a valid item for seed ${seed} (${difficulty})`)
}
